<script lang="ts">
    
    import RouteStartEnd from "../Components/RouteStartEnd.svelte";
    import TopBar from "../Components/TopBar.svelte";
    import type { Route } from '../Model/Route';
    import { fade } from "svelte/transition";
    import DataPill from "../Components/DataPill.svelte";
    import SlideSelect from "../Components/SlideSelect.svelte";
    import SlideOption from "../Components/SlideOption.svelte";
    import { appColor, routesService } from '../Services';
    import { navigate } from "svelte-navigator";
    import AppleWalker from "../Components/AppleWalker.svelte";

    let routeId: string;
    let windowWidth: number = 0;

    export { routeId as id };
    export let route: Route | null = null;

    const MAX_SPEED = 6;

    // TODO: Esta tabla es un invent! La tabla relaciona la velocidad
    // de la persona con su "metabolic equivalent of task (MET)"
    // Hay que establecerla con datos de verdad.
    // https://en.wikipedia.org/wiki/Metabolic_equivalent_of_task
    const velocityMet = {
        4   : 2.95,
        5   : 3.45,
        6   : 4.25,
    };

    let vel: any;
    let weight: any;
    let effortKcal = null;
    let effortTime = null;

    $: if (route) {
        const hours = route.meta.distance / vel;
        effortKcal = Math.round(velocityMet[vel] * weight * hours);
        effortTime = Math.round(hours * 60);    
    }

    $: getRoute(routeId);

    async function getRoute(routeId: string) {
        if (!routeId) {
            route = null;
            return;
        }
        route = await routesService.getRoute(routeId);
        appColor.set(route.color);
    }

    async function createExploration() {
        if (!route) {
            throw new Error('No es posible crear una exploración si no hay una ruta.');
        }
        navigate(`/mapa/${route.id}`);
    }

    function onBack() {
        navigate('/menu');
    }

</script>

<style lang="scss">

    @use '../variables';

    $form-gap: 0.25rem;

    .route-view {
        display: flex;
        flex-direction: column;
        height: var(--vh, 100vh);
        background-color: var(--color-secondary);
    }

    .form-container {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-bottom: 1rem;
    }

    .form-panel {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.5);
        border-bottom-right-radius: 15px;
        border-bottom-left-radius: 15px;
        background-color: #fff;
    }

    .route-image-container {
        position: relative;

        &::before {
            content: '';
            display: block;
            padding-top: 70%;
        }
    }

    .route-image {
        position: absolute;
        top: 0;
        left: 0;
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .route-form {
        position: relative;
        padding: 1rem;
        padding-bottom: 2rem;
    }

    .form-head {
        position: relative;
        z-index: 2;
        margin-top: -45%;
        display: flex;
        flex-direction: column;
        align-items: center;

        h2 {
            text-align: center;
            margin-top: 0;
            font-size: 1.5rem;
            color: var(--route-color, --color-primary);
        }
    }

    .route-name {
        line-height: 1.1;
    }

    .route-meta {
        margin-top: $form-gap;
        display: flex;
        gap: $form-gap;
    }

    .route-meta-pill {
        width: 50%;
    }

    .slide-select {
        margin-top: $form-gap;
    }

    .effort-section {
        display: flex;
        gap: 0.25rem;
    }

    .effort {
        flex: 1 1 0;
        opacity: 0.6;
        margin-top: $form-gap;
    }

    .option-label {
        display: flex;
        align-items: center;
        justify-content: space-between;

        img {
            $size: 2.2rem;
            width: $size;
            height: $size;
            object-fit: contain;
        }

    }

    .btns {
        position: absolute;
        bottom: -1.1rem;
        left: 10px;
        right: 10px;
        display: flex;
        justify-content: center;
        gap: 0.5rem;
        
        button {
            border: 2px solid #fff;
            background-color: var(--route-color, --color-primary);
            color: #fff;
            font-weight: bold;
            padding: 0.5rem 1.5rem;
            font-size: 1rem;
            border-radius: 10px;
            box-shadow: 0 2px 4px rgba($color: #000000, $alpha: 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    @media (max-width: variables.$big-phone-width) {

        .route-name {
            margin-bottom: 0.5rem;
        }

        .option-label {

            img {
                $size: 1.6rem;
                width: $size;
                height: $size;
            }

        }
        
    }

    @media (max-width: variables.$big-phone-width) and (min-height: variables.$big-phone-height) {

        .route-meta {
            flex-wrap: wrap;
        }
        
        .effort-section {
            flex-wrap: wrap;
            gap: 0;
        }

        .route-meta-pill,
        .effort {
            flex: none;
            width: 100%;
        }

    }

</style>

<svelte:window bind:innerWidth={windowWidth} />

{#if route}
<div class="route-view view-container" transition:fade>

    <TopBar title={route.name} on:back={onBack} />

    <div class="form-container">

        <div class="form-panel view-content">

            <div class="route-image-container">
                <img src={route.image} alt="{route.name}" class="route-image">
            </div>

            <div class="route-form">
                <div class="form-head">
                    <AppleWalker speed={(vel / MAX_SPEED) ** 2} />
                    <h2 class="route-name">
                        <span class="d-none big-phone-d-inline">{route.name}:<br></span>
                        {route.surname}
                    </h2>
                </div>
    
                <RouteStartEnd {route} />
                
                <div class="route-meta">
                    <div class="route-meta-pill">
                        <DataPill icon="/images/icon-track.svg" value={`${route.meta.distance} km`} />
                    </div>
                    <div class="route-meta-pill">
                        <DataPill icon="/images/icon-unevenness.svg" value={`${route.meta.unevenness} m`} />
                    </div>
                </div>

                <div class="slide-select">
                    <SlideSelect bind:value={vel}>
                        <SlideOption value={4}>
                            <div class="option-label"><img src="/images/icono-intensidad-oso.svg" alt="Lento"> 4 Km/h</div>
                        </SlideOption>
                        <SlideOption value={5}>
                            <div class="option-label"><img src="/images/icono-intensidad-lobo.svg" alt="Medio"> 5 Km/h</div>
                        </SlideOption>
                        <SlideOption value={6}>
                            <div class="option-label"><img src="/images/icono-intensidad-guepardo.svg" alt="Rápido"> 6 Km/h</div>
                        </SlideOption>
                    </SlideSelect>
                </div>

                <div class="slide-select">
                    <SlideSelect bind:value={weight}>
                        <SlideOption value={45}>
                            <div class="option-label"><img src="/images/icon-weight.svg" alt="Peso"> 40-50 Kgs</div>
                        </SlideOption>
                        <SlideOption value={55}>
                            <div class="option-label"><img src="/images/icon-weight.svg" alt="Peso"> 50-60 Kgs</div>
                        </SlideOption>
                        <SlideOption value={65}>
                            <div class="option-label"><img src="/images/icon-weight.svg" alt="Peso"> 60-70 Kgs</div>
                        </SlideOption>
                        <SlideOption value={75}>
                            <div class="option-label"><img src="/images/icon-weight.svg" alt="Peso"> 70-80 Kgs</div>
                        </SlideOption>
                        <SlideOption value={85}>
                            <div class="option-label"><img src="/images/icon-weight.svg" alt="Peso"> 80-90 Kgs</div>
                        </SlideOption>
                        <SlideOption value={95}>
                            <div class="option-label"><img src="/images/icon-weight.svg" alt="Peso"> 90-100 Kgs</div>
                        </SlideOption>
                        <SlideOption value={105}>
                            <div class="option-label"><img src="/images/icon-weight.svg" alt="Peso"> 100-110 Kgs</div>
                        </SlideOption>
                        <SlideOption value={115}>
                            <div class="option-label"><img src="/images/icon-weight.svg" alt="Peso"> 110-120 Kgs</div>
                        </SlideOption>
                        <SlideOption value={125}>
                            <div class="option-label"><img src="/images/icon-weight.svg" alt="Peso"> >120 Kg</div>
                        </SlideOption>
                    </SlideSelect>
                </div>

                <div class="effort-section">
                    <div class="effort effort-kcal">
                        <DataPill
                            color="#fff"
                            bgColor="var(--color-secondary)"
                            icon="/images/icon-energy.svg"
                            value={`${effortKcal} Kcal`} />
                    </div>
    
                    <div class="effort effort-time">
                        <DataPill
                            color="#fff"
                            bgColor="var(--color-secondary)"
                            icon="/images/icon-clock.svg"
                            value={`${effortTime} Minutos`} />
                    </div>
                </div>
                
                <div class="btns">
                    <!-- <button type="button" class="race-btn one-line" on:click={createRace}>
                        <img src="/images/icon-clock.png" alt=""> Comenzar ruta
                    </button> -->
                    <button type="button" class="explore-btn one-line" on:click={createExploration}>
                        Acceder a la ruta
                    </button>
                </div>

            </div>

        </div>

    </div>

</div>
{/if}
